import React from "react";
import {
    Box,
    Center,
    Heading,
    Card,
    CardBody
} from "@chakra-ui/react";
import BasicCountTable from "./BasicCountTable.js";
import CoursewiseCount from "./reports/CoursewiseCount.js";
import LeadsUsersCoursewiseCount from "./reports/LeadsUsersCoursewiseCount.js";
import PotentialsUsersCoursewiseCount from "./reports/PotentialsUsersCoursewiseCount.js";
import moment from "moment";
import { getCurrentIntakeQuery, getCurrentIntakeMonth, getCurrentIntakeYear } from "../utils/Dates.js";

const DetailContent = ({ matrix1Ref, matrix2Ref, matrix3Ref, matrix4Ref, reportsMatrix2 }) => {
    const containerStyle = {
        borderRadius: 5,
        bg: 'gray.100',
        color: 'blue.900',
        style: {
            padding: 5
        }
    };
    const headerStyle = {
        as: 'h1',
        size: 'md',
        textAlign: 'center'
    };

    const intakeQuery = `AND ${getCurrentIntakeQuery()}`;

    const reportsMatrix1 = [
        {
            title: 'Total Available Leads',
            module: 'Leads',
            query: `leadstatus != 'Converted' AND course_type = 'Main Course' `,
            subject: 'leads_count_except_converted',
            color: '#285729'
        },
        {
            title: ' -- Open ',
            module: 'Leads',
            query: `leadstatus = 'Open' AND course_type = 'Main Course'`,
            subject: 'leads_count_open',
            color: '#3ea2d4'
        },
        {
            title: ' -- Following Up ',
            module: 'Leads',
            query: `leadstatus = 'Following' AND course_type = 'Main Course'`,
            subject: 'leads_count_following',
            color: '#54c7a4'
        },
        {
            title: ' -- Not Qualified ',
            module: 'Leads',
            query: `leadstatus IN ('Junk', 'Not Interested') AND course_type = 'Main Course'`
        },
        {
            title: 'Total Potential Students',
            module: 'Potentials',
            query: `potential_stage != 'Enrolled' AND student_eligibility !='SEC' ${intakeQuery}`,
            subject: 'potentials_count_total',
            color: '#08f527'
        },
        {
            title: ' -- Following up ',
            module: 'Potentials',
            query: `potential_stage = 'Followup' AND student_eligibility !='SEC'  ${intakeQuery}`,
            subject: 'potentials_count_total',
            color: '#08f527'
        },
        {
            title: ' -- Registration form sent (RFS)',
            module: 'Potentials',
            query: `potential_stage = 'Registration'  AND student_eligibility !='SEC'  ${intakeQuery}`,
            subject: 'potentials_count_registration',
            color: '#e39acb'
        },
        {
            title: ' -- Registration Payment Pending (RPP) ',
            module: 'Potentials',
            query: `potential_stage = 'Registration Payment Pending'  AND student_eligibility !='SEC'  ${intakeQuery}`,
            subject: 'potentials_count_registration_payment_pending',
        },
        {
            title: ' -- Registration Payment Confirmed (RPC)',
            module: 'Potentials',
            query: `potential_stage = 'Registration Paid'  AND student_eligibility !='SEC'  ${intakeQuery}`,
            subject: 'potentials_count_registration_paid',
            color: '#e8157e'
        },
        {
            title: 'Total Candidates',
            module: 'Candidates',
            query: `candidate_status != 'Enrolled'   AND student_eligibility !='SEC'   ${intakeQuery}`,
            subject: 'candidates_count_total'
        },
        {
            title: ' -- Aptitude Test Pending',
            module: 'Candidates',
            query: `candidate_status = 'Followup'   AND student_eligibility !='SEC'   ${intakeQuery}`,
            subject: 'candidates_count_aptitude_pending'
        },
        {
            title: ' -- Aptitude Test Scheduled',
            module: 'Candidates',
            query: `candidate_status = 'Aptitude Scheduled'   AND student_eligibility !='SEC'   ${intakeQuery}`,
            subject: 'candidates_count_aptitude_scheduled'
        },
        {
            title: ' -- Aptitude Test Passed',
            module: 'Candidates',
            query: `candidate_status = 'Aptitude Passed'   AND student_eligibility !='SEC'   ${intakeQuery}`,
            subject: 'candidates_count_aptitude_passed'
        },
        {
            title: ' -- Aptitude Test Failed',
            module: 'Candidates',
            query: `candidate_status = 'Aptitude Failed'   AND student_eligibility !='SEC'   ${intakeQuery}`,
            subject: 'candidates_count_aptitude_failed'
        },
        {
            title: ' -- Enrollment Payment Form Sent',
            module: 'Candidates',
            query: `candidate_status = 'Pending payment CERT III'   AND student_eligibility !='SEC'   ${intakeQuery}`,
            subject: 'candidates_count_pending_payment_cert_iii',
            color: '#967333'
        },
        {
            title: ' -- Enrollment Payment Confirmation Pending',
            module: 'Candidates',
            query: `candidate_status = 'CERT III Paid' AND cert_iii_payment = 'Pending'   AND student_eligibility !='SEC'   ${intakeQuery}`,
            subject: 'candidates_count_pending_payment_confirmation_cert_iii',
            color: '#5ce05a'
        },
        {
            title: ' -- Enrollment Payment Paid ',
            module: 'Candidates',
            query: `candidate_status = 'CERT III Paid' AND cert_iii_payment = 'Paid'   AND student_eligibility !='SEC'   ${intakeQuery}`,
            subject: 'candidates_count_paid_payment_cert_iii',
            color: '#5ce05a'
        },
        {
            title: 'Total Students',
            module: 'Students',
            query: `${intakeQuery.replace('AND ', '')}  AND student_eligibility !='SEC' `,
            subject: 'students_count_total'
        }
    ];

    const reportsMatrix = {
        course_cc: 'Commercial Cookery [Certificate IV] - (Full time - 1.5 years)',
        course_hm: 'Hospitality Management [Advanced Diploma] - (Full time - 2 years)',
        course_pc: 'Patisserie [Certificate IV] - (Full time - 1.5 years)',
        course_em: 'Event Management [Diploma] - (Part time - 2 years)',
        course_tt: 'Travel and Tourism Management [Advanced Diploma] - (Full time - 1.5 years)'
    };

    return (
        <Box fontSize={{ base: '16px', xs: '12px', sm: '14px', md: '16px' }} >
            <Box {...containerStyle}>
                <Center paddingY={5}>
                    <Heading ref={matrix1Ref} {...headerStyle}>Intake Snapshot, {getCurrentIntakeMonth()} {getCurrentIntakeYear()}</Heading>
                </Center>
                <Card
                    direction={{ base: 'column', sm: 'row' }}
                    overflow='hidden'
                    variant='outline'
                    align='center'
                    borderRadius={10}
                    marginX={{ base: '5px', sm: '0px', md: '5px', lg: '5px' }}
                >
                    <CardBody>
                        <BasicCountTable type={'dr'} reportsMatrix={reportsMatrix1} />
                    </CardBody>
                </Card>
            </Box>
            <Box {...containerStyle}>
                <Center paddingY={5}>
                    <Heading ref={matrix2Ref} {...headerStyle}>Intake Snapshot Course-wise, {getCurrentIntakeMonth()} {getCurrentIntakeYear()}</Heading>
                </Center>
                <Card
                    direction={{ base: 'column', sm: 'row' }}
                    overflowX='auto'
                    variant='outline'
                    align='center'
                    borderRadius={10}
                    marginX={{ base: '5px', sm: '0px', md: '5px', lg: '5px' }}
                >
                    <CardBody paddingX={{ base: '5px', sm: '8px', md: '1px' }} overflowX="auto">
                        <CoursewiseCount matrix={reportsMatrix2} module='Leads' query={{ intake: getCurrentIntakeMonth(), intakeyear: getCurrentIntakeYear() }} />
                    </CardBody>
                </Card>
            </Box>
            <Box {...containerStyle}>
                <Center paddingY={5}>
                    <Heading ref={matrix3Ref} {...headerStyle}>Leads Snapshot Course-wise, {getCurrentIntakeMonth()} {getCurrentIntakeYear()}</Heading>
                </Center>
                <Card
                    direction={{ base: 'column', sm: 'row' }}
                    overflow='hidden'
                    variant='outline'
                    align='center'
                    borderRadius={10}
                    marginX={{ base: '5px', sm: '0px', md: '5px', lg: '5px' }}
                >
                    <CardBody>
                        <LeadsUsersCoursewiseCount matrix={reportsMatrix} module='Leads' />
                    </CardBody>
                </Card>
            </Box>
            <Box {...containerStyle}>
                <Center paddingY={5}>
                    <Heading ref={matrix4Ref} {...headerStyle}>Performance of the student counselors</Heading>
                </Center>
                <PotentialsUsersCoursewiseCount module={'Potentials'} hide={false} query={{ from: moment().subtract(7, 'days').format('YYYY-MM-DD'), to: moment().format('YYYY-MM-DD') }} />
            </Box>
        </Box>
    );
};

export default DetailContent;
